/**
 * Excel export for COD reports. exceljs is loaded on demand so it never lands in the main bundle.
 */
import type { TFunction } from "i18next";
import { CodOrder, CodPaymentTransaction } from "../types/codSalesRep";
import { codInvoiceStatusKey, getOrderOutstanding, getOrderPayableTotal } from "./codUtils";
import { getOrderCollected, InvoiceIndex, lineTotal, orderBucket, orderDate, orderFinancials } from "./codReports";

export type ExcelColumnType = "text" | "money" | "int" | "date" | "datetime" | "percent";

export interface ExcelColumn {
  key: string;
  header: string;
  type?: ExcelColumnType;
  width?: number;
}

export interface ExcelSheet {
  name: string;
  columns: ExcelColumn[];
  rows: Record<string, unknown>[];
  /** Optional totals row (keys matching columns). */
  totals?: Record<string, unknown>;
}

const INK = "FF162D3A";
const MUTED = "FFF3F7FB";
const BORDER = "FFE6EAF1";

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Excel sheet names: max 31 chars, no []:*?/\ */
const safeSheetName = (name: string) => name.replace(/[[\]:*?/\\]/g, " ").slice(0, 31);

export const exportExcelWorkbook = async (
  fileName: string,
  sheets: ExcelSheet[],
  options: { rtl?: boolean; creator?: string } = {}
) => {
  const mod = await import("exceljs/dist/exceljs.min.js");
  const ExcelJS = (mod.default ?? mod) as typeof import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = options.creator ?? "Al Hayat Portal";
  workbook.created = new Date();

  sheets.forEach((spec) => {
    const sheet = workbook.addWorksheet(safeSheetName(spec.name), {
      views: [{ state: "frozen", ySplit: 1, rightToLeft: Boolean(options.rtl) }],
    });

    sheet.columns = spec.columns.map((col) => ({
      key: col.key,
      header: col.header,
      width:
        col.width ??
        (col.type === "money" ? 18 : col.type === "date" ? 14 : col.type === "datetime" ? 20 : col.type === "int" || col.type === "percent" ? 12 : 24),
    }));

    const format = (col: ExcelColumn, value: unknown) => {
      if (value === null || value === undefined || value === "") return null;
      switch (col.type) {
        case "money":
        case "int":
        case "percent": {
          const num = Number(value);
          return Number.isFinite(num) ? num : null;
        }
        case "date":
        case "datetime":
          return toDate(value);
        default:
          return String(value);
      }
    };

    spec.rows.forEach((row) => {
      sheet.addRow(Object.fromEntries(spec.columns.map((col) => [col.key, format(col, row[col.key])])));
    });

    if (spec.totals) {
      const totalsRow = sheet.addRow(
        Object.fromEntries(spec.columns.map((col) => [col.key, format(col, spec.totals![col.key])]))
      );
      totalsRow.font = { bold: true, color: { argb: INK } };
      totalsRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: MUTED } };
        cell.border = { top: { style: "thin", color: { argb: INK } } };
      });
    }

    spec.columns.forEach((col, index) => {
      const column = sheet.getColumn(index + 1);
      if (col.type === "money") column.numFmt = "#,##0.00";
      if (col.type === "int") column.numFmt = "0";
      if (col.type === "percent") column.numFmt = "0.0%";
      if (col.type === "date") column.numFmt = "yyyy-mm-dd";
      if (col.type === "datetime") column.numFmt = "yyyy-mm-dd hh:mm";
    });

    const header = sheet.getRow(1);
    header.height = 22;
    header.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: INK } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.border = { bottom: { style: "thin", color: { argb: BORDER } } };
    });

    if (spec.rows.length > 0) {
      sheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1 + spec.rows.length, column: spec.columns.length },
      };
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const reportFileStamp = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
};

/* ── Shared detail sheets (used by both rep and manager reports) ───────────────── */

const approvalLabel = (t: TFunction, order: CodOrder) => {
  const bucket = orderBucket(order);
  if (bucket === "pending") return t("codSalesRep.pendingReview");
  if (bucket === "rejected") return t("codSalesRep.rejected");
  return t("codSalesRep.approved");
};

const paymentLabel = (t: TFunction, order: CodOrder, index?: InvoiceIndex) => {
  if (orderBucket(order) !== "approved") return "—";
  const money = orderFinancials(order, index);
  if (money.fromInvoices) {
    // Paid only when every invoiced line is settled and nothing is still awaiting an invoice.
    if (money.invoiceCount > 0 && money.remaining <= 0.005 && money.notInvoiced <= 0.005) return t("codSalesRep.paid");
    if (money.collected > 0.005) return t("codSalesRep.partlyPaid");
    if (money.invoiceCount === 0) return t("codSalesRep.awaitingSageInvoice");
    return t("codSalesRep.awaitingPayment");
  }
  const payable = getOrderPayableTotal(order);
  const collected = getOrderCollected(order);
  if (payable > 0 && getOrderOutstanding(order) <= 0.005) return t("codSalesRep.paid");
  if (collected > 0) return t("codSalesRep.partlyPaid");
  return t("codSalesRep.awaitingPayment");
};

export const buildOrdersSheet = (
  t: TFunction,
  orders: CodOrder[],
  options: { includeRep?: boolean; invoiceIndex?: InvoiceIndex } = {}
): ExcelSheet => {
  const index = options.invoiceIndex;
  const columns: ExcelColumn[] = [
    { key: "number", header: t("codReports.orderNo"), width: 22 },
    { key: "date", header: t("codReports.date"), type: "datetime" },
    ...(options.includeRep ? [{ key: "rep", header: t("codReports.salesRep"), width: 26 }] : []),
    { key: "customerCode", header: t("codReports.customerCode"), width: 18 },
    { key: "customerName", header: t("codReports.customerName"), width: 34 },
    { key: "approval", header: t("codReports.approvalStatus"), width: 18 },
    { key: "payment", header: t("codReports.paymentStatus"), width: 18 },
    { key: "items", header: t("codReports.items"), type: "int" },
    { key: "total", header: t("codReports.total"), type: "money" },
    { key: "invoiced", header: t("codReports.invoiced"), type: "money" },
    { key: "paid", header: t("codReports.paid"), type: "money" },
    { key: "remaining", header: t("codReports.remaining"), type: "money" },
    { key: "notInvoiced", header: t("codReports.notInvoiced"), type: "money", width: 20 },
    { key: "invoiceNumbers", header: t("codReports.invoiceNumbers"), width: 30 },
    { key: "currency", header: t("codReports.currency"), width: 10 },
    { key: "sage", header: t("codReports.sageOrders"), width: 34 },
    { key: "synced", header: t("codReports.syncedToSage"), width: 14 },
    { key: "firstPayment", header: t("codReports.firstPayment"), type: "datetime" },
    { key: "fullyPaid", header: t("codReports.fullyPaid"), type: "datetime" },
    { key: "rejectionReason", header: t("codReports.rejectionReason"), width: 30 },
  ];

  const rows = orders.map((order) => {
    const approved = orderBucket(order) === "approved";
    const money = orderFinancials(order, index);
    const invoiceNumbers = (index?.get(order.id)?.lines ?? [])
      .flatMap((line) => line.invoices.map((invoice) => invoice.invoiceNumber))
      .join(", ");
    return {
      number: order.number,
      date: orderDate(order),
      rep: order.salesRepName || t("codReports.unassigned"),
      customerCode: order.customerCode,
      customerName: order.customerName,
      approval: approvalLabel(t, order),
      payment: paymentLabel(t, order, index),
      items: order.items?.length ?? 0,
      total: getOrderPayableTotal(order),
      invoiced: approved ? money.invoiced : 0,
      paid: approved ? money.collected : 0,
      remaining: approved ? money.remaining : 0,
      notInvoiced: approved ? money.notInvoiced : 0,
      invoiceNumbers,
      currency: order.currency || "IQD",
      sage: (order.sageOrderNumbers ?? []).join(", "),
      synced: order.isSynced ? t("codReports.yes") : t("codReports.no"),
      firstPayment: order.firstPaymentAt,
      fullyPaid: order.fullyPaidAt,
      rejectionReason: order.rejectionReason,
    };
  });

  const sum = (key: "total" | "invoiced" | "paid" | "remaining" | "notInvoiced") =>
    rows.reduce((acc, row) => acc + Number(row[key] ?? 0), 0);

  return {
    name: t("codReports.sheetOrders"),
    columns,
    rows,
    totals: {
      number: t("codReports.totalsRow"),
      items: rows.reduce((acc, row) => acc + row.items, 0),
      total: sum("total"),
      invoiced: sum("invoiced"),
      paid: sum("paid"),
      remaining: sum("remaining"),
      notInvoiced: sum("notInvoiced"),
    },
  };
};

/** One row per Sage invoice (Order → Line → Invoice); lines not invoiced yet are listed too. */
export const buildInvoicesSheet = (
  t: TFunction,
  orders: CodOrder[],
  index: InvoiceIndex,
  options: { includeRep?: boolean } = {}
): ExcelSheet => {
  const rows: Record<string, unknown>[] = [];
  orders.forEach((order) => {
    const entry = index.get(order.id);
    if (!entry) return;
    entry.lines.forEach((line) => {
      const base = {
        orderNo: entry.orderNumber || order.number,
        orderDate: entry.orderDate ?? orderDate(order),
        rep: order.salesRepName || t("codReports.unassigned"),
        customerCode: entry.customerCode || order.customerCode,
        customerName: entry.customerName || order.customerName,
        line: line.reference,
        category: line.category,
        lineTotal: line.lineTotal,
        currency: entry.currency || "IQD",
      };
      if (!line.hasInvoice || line.invoices.length === 0) {
        rows.push({ ...base, invoiceNo: t("codSalesRep.awaitingSageInvoice") });
        return;
      }
      line.invoices.forEach((invoice) => {
        rows.push({
          ...base,
          invoiceNo: invoice.invoiceNumber,
          invoiceDate: invoice.invoiceDate,
          dueDate: invoice.dueDate,
          amount: invoice.amount,
          paid: invoice.paidAmount,
          remaining: invoice.remainingAmount,
          status: t(codInvoiceStatusKey(invoice.paymentStatus)),
        });
      });
    });
  });

  const sum = (key: string) => rows.reduce((acc, row) => acc + Number(row[key] ?? 0), 0);

  return {
    name: t("codReports.sheetInvoices"),
    columns: [
      { key: "orderNo", header: t("codReports.orderNo"), width: 22 },
      { key: "orderDate", header: t("codReports.date"), type: "date" },
      ...(options.includeRep ? [{ key: "rep", header: t("codReports.salesRep"), width: 26 }] : []),
      { key: "customerCode", header: t("codReports.customerCode"), width: 18 },
      { key: "customerName", header: t("codReports.customerName"), width: 34 },
      { key: "line", header: t("codReports.line"), width: 24 },
      { key: "category", header: t("codReports.category"), width: 22 },
      { key: "lineTotal", header: t("codReports.lineTotal"), type: "money" },
      { key: "invoiceNo", header: t("codReports.invoiceNo"), width: 22 },
      { key: "invoiceDate", header: t("codReports.invoiceDate"), type: "date" },
      { key: "dueDate", header: t("codReports.dueDate"), type: "date" },
      { key: "amount", header: t("codReports.invoiceAmount"), type: "money" },
      { key: "paid", header: t("codReports.paid"), type: "money" },
      { key: "remaining", header: t("codReports.remaining"), type: "money" },
      { key: "status", header: t("codReports.status"), width: 16 },
      { key: "currency", header: t("codReports.currency"), width: 10 },
    ],
    rows,
    totals: {
      orderNo: t("codReports.totalsRow"),
      amount: sum("amount"),
      paid: sum("paid"),
      remaining: sum("remaining"),
    },
  };
};

export const buildLinesSheet = (
  t: TFunction,
  orders: CodOrder[],
  options: { includeRep?: boolean } = {}
): ExcelSheet => {
  const rows = orders.flatMap((order) =>
    (order.items ?? []).map((item) => ({
      number: order.number,
      date: orderDate(order),
      rep: order.salesRepName || t("codReports.unassigned"),
      customerCode: order.customerCode,
      customerName: order.customerName,
      approval: approvalLabel(t, order),
      productCode: item.code || item.productId,
      productName: item.name,
      qty: item.quantity,
      unitPrice: item.price,
      lineTotal: lineTotal(item),
    }))
  );

  return {
    name: t("codReports.sheetLines"),
    columns: [
      { key: "number", header: t("codReports.orderNo"), width: 22 },
      { key: "date", header: t("codReports.date"), type: "datetime" },
      ...(options.includeRep ? [{ key: "rep", header: t("codReports.salesRep"), width: 26 }] : []),
      { key: "customerCode", header: t("codReports.customerCode"), width: 18 },
      { key: "customerName", header: t("codReports.customerName"), width: 34 },
      { key: "approval", header: t("codReports.approvalStatus"), width: 18 },
      { key: "productCode", header: t("codReports.productCode"), width: 16 },
      { key: "productName", header: t("codReports.productName"), width: 40 },
      { key: "qty", header: t("codReports.qty"), type: "int" },
      { key: "unitPrice", header: t("codReports.unitPrice"), type: "money" },
      { key: "lineTotal", header: t("codReports.lineTotal"), type: "money" },
    ],
    rows,
    totals: {
      number: t("codReports.totalsRow"),
      qty: rows.reduce((acc, row) => acc + Number(row.qty ?? 0), 0),
      lineTotal: rows.reduce((acc, row) => acc + Number(row.lineTotal ?? 0), 0),
    },
  };
};

export const buildPaymentsSheet = (
  t: TFunction,
  payments: CodPaymentTransaction[],
  allOrders: CodOrder[],
  options: { includeRep?: boolean } = {}
): ExcelSheet => {
  const orderById = new Map(allOrders.map((order) => [order.id, order]));
  const rows = payments.map((payment) => {
    const order = orderById.get(payment.orderId);
    return {
      date: payment.paymentDate,
      receipt: payment.documentReceiptNumber,
      reference: payment.paymentReference || "",
      allocations: (payment.invoices ?? [])
        .map((inv) => `${inv.invoiceNumber}: ${Number(inv.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`)
        .join("; "),
      orderNo: order?.number ?? payment.orderId,
      rep: order?.salesRepName || t("codReports.unassigned"),
      customerCode: payment.customerCode || order?.customerCode,
      customerName: order?.customerName,
      amount: payment.amount,
      partial: payment.isPartialPayment ? t("codReports.yes") : t("codReports.no"),
      orderTotal: payment.orderTotal,
      remainingAfter: payment.remainingAfterPayment,
      currency: payment.currency || "IQD",
      status: payment.status,
      transactionId: payment.transactionId,
      sagePayment: payment.sagePaymentNumber,
      synced: payment.isSynced ? t("codReports.yes") : t("codReports.no"),
    };
  });

  return {
    name: t("codReports.sheetPayments"),
    columns: [
      { key: "date", header: t("codReports.paymentDate"), type: "datetime" },
      { key: "receipt", header: t("codReports.receiptNo"), width: 18 },
      { key: "reference", header: t("codReports.paymentReference"), width: 26 },
      { key: "allocations", header: t("codReports.invoicesPaid"), width: 40 },
      { key: "orderNo", header: t("codReports.orderNo"), width: 22 },
      ...(options.includeRep ? [{ key: "rep", header: t("codReports.salesRep"), width: 26 }] : []),
      { key: "customerCode", header: t("codReports.customerCode"), width: 18 },
      { key: "customerName", header: t("codReports.customerName"), width: 34 },
      { key: "amount", header: t("codReports.amount"), type: "money" },
      { key: "partial", header: t("codReports.partial"), width: 10 },
      { key: "orderTotal", header: t("codReports.orderTotal"), type: "money" },
      { key: "remainingAfter", header: t("codReports.remainingAfter"), type: "money" },
      { key: "currency", header: t("codReports.currency"), width: 10 },
      { key: "status", header: t("codReports.status"), width: 14 },
      { key: "transactionId", header: t("codReports.transactionId"), width: 28 },
      { key: "sagePayment", header: t("codReports.sagePaymentNo"), width: 22 },
      { key: "synced", header: t("codReports.syncedToSage"), width: 14 },
    ],
    rows,
    totals: {
      date: t("codReports.totalsRow"),
      amount: rows.reduce((acc, row) => acc + Number(row.amount ?? 0), 0),
    },
  };
};
